/* Peptide Guide theme — carousels, search/filter, nav toggle, EN/ES translation. Vanilla JS, no dependencies. */
(function () {
  "use strict";

  var DATA = window.PEPTIDE_DATA || { peptides: [], blends: [], buckets: {} };

  /* ================= Language state ================= */
  var LANG = "en";
  try {
    var savedLang = window.localStorage.getItem("peptideGuideLang");
    if (savedLang === "es" || savedLang === "en") LANG = savedLang;
  } catch (e) { /* localStorage unavailable — default to en */ }

  var REFRESH_CALLBACKS = [];

  var UI_STRINGS = {
    en: {
      purpose: "Purpose:",
      sideEffects: "Side effects:",
      emptyCategory: "No entries in this category yet.",
      emptySearch: "No peptides match your search.",
      countTemplate: "{shown} of {total} entries shown",
      pillAll: "All",
      pillBlends: "Blends",
      structureAlt: "Chemical structure of {name}",
      structureCaption: "Structure via PubChem",
      mechanismLabel: "How it works:",
      formInjection: "💉 Injection",
      formTablet: "💊 Tablet / Capsule",
      formTopical: "🧴 Topical only",
      detailBack: "← Back to all peptides",
      detailLegalHeading: "Legal & availability status",
      detailFormsHeading: "How it's typically sold",
      detailMoreHeading: "Additional notes",
      detailRelatedHeading: "Related in this category",
      detailCidLabel: "View on PubChem (CID {cid})",
      priceCardLabel: "Typical price:",
      priceHeading: "Typical price range (so you don't get scammed)",
      priceNoRangeHeading: "About pricing",
      priceDisclaimer: "Prices vary by vendor and change often — treat this as a ballpark, not a quote. This site doesn't sell anything and doesn't endorse any vendor. If a seller quotes you a price far outside this range in either direction, that's a reason to slow down and double-check who you're buying from."
    },
    es: {
      purpose: "Propósito:",
      sideEffects: "Efectos secundarios:",
      emptyCategory: "Aún no hay entradas en esta categoría.",
      emptySearch: "Ningún péptido coincide con tu búsqueda.",
      countTemplate: "{shown} de {total} entradas mostradas",
      pillAll: "Todos",
      pillBlends: "Mezclas",
      structureAlt: "Estructura química de {name}",
      structureCaption: "Estructura vía PubChem",
      mechanismLabel: "Cómo funciona:",
      formInjection: "💉 Inyección",
      formTablet: "💊 Tableta / Cápsula",
      formTopical: "🧴 Solo tópico",
      detailBack: "← Volver a todos los péptidos",
      detailLegalHeading: "Estatus legal y de disponibilidad",
      detailFormsHeading: "Cómo se vende típicamente",
      detailMoreHeading: "Notas adicionales",
      detailRelatedHeading: "Relacionados en esta categoría",
      detailCidLabel: "Ver en PubChem (CID {cid})",
      priceCardLabel: "Precio típico:",
      priceHeading: "Rango de precio típico (para que no te estafen)",
      priceNoRangeHeading: "Sobre el precio",
      priceDisclaimer: "Los precios varían según el proveedor y cambian con frecuencia — tómalo como una referencia aproximada, no una cotización. Este sitio no vende nada y no respalda a ningún proveedor. Si un vendedor te cotiza un precio muy fuera de este rango, en cualquier dirección, es motivo para detenerte y verificar bien a quién le estás comprando."
    }
  };

  /* ================= Status / forms explanatory copy (detail page) ================= */
  var STATUS_DETAIL_COPY = {
    approved: {
      en: "This compound is FDA-approved as a prescription medicine for at least one indication in the United States. That does not mean any particular off-label use is approved or officially recommended — an approved drug used off-label (for example, for bodybuilding or a non-approved weight-loss purpose) has not been evaluated for that use, and should not be assumed to carry the same risk/benefit profile as its approved use.",
      es: "Este compuesto está aprobado por la FDA como medicamento de venta con receta para al menos una indicación en Estados Unidos. Eso no significa que un uso fuera de indicación en particular esté aprobado o recomendado oficialmente — un medicamento aprobado usado fuera de indicación (por ejemplo, para culturismo o un propósito de pérdida de peso no aprobado) no ha sido evaluado para ese uso, y no debe asumirse que conlleva el mismo perfil de riesgo/beneficio que su uso aprobado."
    },
    investigational: {
      en: "This compound is currently in human clinical trials but has not yet received full regulatory approval for any indication. Everything known about its effects comes from a limited number of trial participants under close medical supervision — real-world, unsupervised use carries risks that clinical trials are specifically designed to catch and manage.",
      es: "Este compuesto se encuentra actualmente en ensayos clínicos en humanos, pero aún no ha recibido la aprobación regulatoria completa para ninguna indicación. Todo lo que se sabe sobre sus efectos proviene de un número limitado de participantes de ensayos bajo estrecha supervisión médica — el uso real y sin supervisión conlleva riesgos que los ensayos clínicos están diseñados específicamente para detectar y manejar."
    },
    research: {
      en: "This is not an FDA-approved drug. It is typically sold labeled “for laboratory / research use only, not for human consumption” — a legal labeling technicality vendors use to sell it without going through drug-approval regulation. Products sold this way are not verified by any regulatory body for purity, sterility, or accurate dosing.",
      es: "Este no es un medicamento aprobado por la FDA. Normalmente se vende etiquetado como “solo para uso de laboratorio/investigación, no para consumo humano” —un tecnicismo legal de etiquetado que los proveedores usan para venderlo sin pasar por la regulación de aprobación de medicamentos. Los productos vendidos de esta forma no están verificados por ningún organismo regulador en cuanto a pureza, esterilidad o dosificación precisa."
    },
    cosmetic: {
      en: "This ingredient is formulated and studied for topical/cosmetic use only. It has not been tested or approved for injection or oral consumption, and using a cosmetic-grade ingredient that way carries risks that have not been studied.",
      es: "Este ingrediente está formulado y estudiado únicamente para uso tópico/cosmético. No ha sido probado ni aprobado para inyección o consumo oral, y usar un ingrediente de grado cosmético de esa forma conlleva riesgos que no han sido estudiados."
    },
    caution: {
      en: "This entry carries serious safety and/or legal concerns beyond the usual research-chemical caveats — read the side-effects section carefully before going any further.",
      es: "Esta entrada conlleva serias preocupaciones de seguridad y/o legales más allá de las advertencias habituales de los productos químicos de investigación — lee con atención la sección de efectos secundarios antes de continuar."
    },
    blend: {
      en: "This is a pre-mixed combination product, not a single approved drug. The combination itself — as a mixture — has typically not been studied; only its individual components have their own (often limited) research base. See each component's own entry for its individual profile.",
      es: "Este es un producto de combinación premezclada, no un solo medicamento aprobado. La combinación en sí —como mezcla— generalmente no ha sido estudiada; solo sus componentes individuales tienen su propia base de investigación (a menudo limitada). Consulta la entrada individual de cada componente para su perfil propio."
    },
    supplement: {
      en: "This product can lawfully be sold as a dietary supplement in the United States. That is a different (and much lower) bar than FDA drug approval — supplements are not required to prove efficacy before going to market, and manufacturing quality varies by brand.",
      es: "Este producto puede venderse legalmente como suplemento dietético en Estados Unidos. Ese es un estándar diferente (y mucho más bajo) que la aprobación de medicamentos de la FDA — a los suplementos no se les exige demostrar eficacia antes de salir al mercado, y la calidad de fabricación varía según la marca."
    },
    controlled: {
      en: "In the United States, this compound is a Schedule III controlled substance under the Anabolic Steroid Control Act. Simple possession without a valid prescription is a federal crime, and manufacturing or distributing it carries felony-level penalties. It may have a legitimate FDA-approved medical use (for example, hypogonadism) under a doctor's supervision — but that is a completely different context from unsupervised bodybuilding/performance use, which is both illegal and medically unsupervised.",
      es: "En Estados Unidos, este compuesto es una sustancia controlada de Lista III bajo la Ley de Control de Esteroides Anabólicos (Anabolic Steroid Control Act). La simple posesión sin una receta válida es un delito federal, y su fabricación o distribución conlleva penas de nivel de delito grave (felony). Puede tener un uso médico legítimo aprobado por la FDA (por ejemplo, para el hipogonadismo) bajo supervisión de un médico — pero ese es un contexto completamente distinto al uso no supervisado en culturismo/rendimiento, que es a la vez ilegal y sin supervisión médica."
    }
  };

  var FORM_DETAIL_COPY = {
    injection: {
      en: "Injectable forms carry needle-related risks (infection, abscess, scar tissue) in addition to the compound's own effects, and injectable products bought outside a licensed pharmacy are not verified for sterility or accurate concentration.",
      es: "Las formas inyectables conllevan riesgos relacionados con las agujas (infección, absceso, tejido cicatricial) además de los efectos propios del compuesto, y los productos inyectables comprados fuera de una farmacia con licencia no están verificados en cuanto a esterilidad o concentración precisa."
    },
    tablet: {
      en: "Oral/tablet forms pass through the digestive system and liver before reaching circulation (first-pass metabolism), which can reduce bioavailability or, for certain compounds, place extra metabolic burden on the liver — see this entry's side-effects section for specifics.",
      es: "Las formas orales/en tableta pasan por el sistema digestivo y el hígado antes de llegar a la circulación (metabolismo de primer paso), lo que puede reducir la biodisponibilidad o, en ciertos compuestos, imponer una carga metabólica adicional sobre el hígado — consulta la sección de efectos secundarios de esta entrada para más detalles."
    },
    topical: {
      en: "Topical forms are absorbed through the skin and are generally not intended or tested for injection or oral use.",
      es: "Las formas tópicas se absorben a través de la piel y generalmente no están diseñadas ni probadas para uso inyectable u oral."
    }
  };

  /* ================= Static UI translation dictionary ================= */
  var TRANSLATIONS = {
    es: {
      "nav.categories": "Explorar Categorías",
      "nav.blends": "Mezclas",
      "nav.all": "Todos los Péptidos A-Z",
      "nav.steroids": "Esteroides",

      "announce.text": "Solo referencia educativa — nada en este sitio está a la venta. No es asesoramiento médico.",
      "announce.link": "Lee nuestro aviso completo",

      "section.categories_heading": "Explorar por categoría",

      "cat.healing.title": "Curación y Recuperación",
      "cat.healing.desc": "Péptidos para la reparación de tejido, tendones y revestimiento intestinal.",
      "cat.growth-hormone.title": "Eje de Hormona del Crecimiento",
      "cat.growth-hormone.desc": "Secretagogos y análogos de GHRH/GnRH.",
      "cat.metabolic.title": "Metabólico y GLP-1",
      "cat.metabolic.desc": "Fármacos para el apetito, el azúcar en sangre y el control de peso.",
      "cat.cognitive.title": "Cognitivo, Estrés y Sueño",
      "cat.cognitive.desc": "Péptidos nootrópicos, ansiolíticos y para el sueño.",
      "cat.longevity.title": "Longevidad y Salud Celular",
      "cat.longevity.desc": "Péptidos bioreguladores y de investigación mitocondrial.",
      "cat.sexual-hormonal.title": "Salud Sexual y Hormonal",
      "cat.sexual-hormonal.desc": "Compuestos hormonales reproductivos y de salud sexual.",
      "cat.cosmetic.title": "Cosmético y Piel",
      "cat.cosmetic.desc": "Ingredientes exclusivamente tópicos para el cuidado de la piel.",
      "cat.bone-muscle.title": "Huesos y Músculos",
      "cat.bone-muscle.desc": "Compuestos relacionados con la densidad ósea y la masa muscular.",
      "cat.steroids.title": "Esteroides Anabólicos (EAA)",
      "cat.steroids.desc": "Esteroides de gimnasio/culturismo — qué hacen, y sus riesgos legales y de salud.",
      "cat.other.title": "Inmunológico y Especialidades",
      "cat.other.desc": "Moduladores inmunológicos y compuestos de investigación especializados.",
      "cat.blends.title": "Mezclas Comunes",
      "cat.blends.desc": "Productos premezclados de múltiples péptidos.",
      "cat.all-peptides.title": "Todos los Péptidos A-Z",
      "cat.all-peptides.desc": "Busca o filtra la lista completa.",

      "carousel.healing.desc": "Péptidos para la reparación de tendones, ligamentos, revestimiento intestinal y nervios.",
      "carousel.growth-hormone.desc": "Secretagogos y análogos de GHRH/GnRH que actúan sobre el eje hipofisario.",
      "carousel.metabolic.desc": "Fármacos y análogos hormonales para el apetito, el azúcar en sangre y el control de peso.",
      "carousel.cognitive.desc": "Péptidos nootrópicos, ansiolíticos y relacionados con el sueño.",
      "carousel.longevity.desc": "Péptidos bioreguladores y de investigación mitocondrial/envejecimiento celular.",
      "carousel.sexual-hormonal.desc": "Compuestos hormonales reproductivos y de salud sexual.",
      "carousel.cosmetic.desc": "Ingredientes exclusivamente tópicos usados en formulaciones para el cuidado de la piel.",
      "carousel.bone-muscle.desc": "Compuestos relacionados con la densidad ósea y la masa muscular.",
      "carousel.steroids.desc": "Esteroides de gimnasio/culturismo usados fuera de indicación para músculo y fuerza — qué hacen, y sus serios riesgos legales y de salud.",
      "carousel.other.desc": "Moduladores inmunológicos y compuestos de investigación especializados — lee las advertencias con atención.",
      "carousel.blends.desc": "Combinaciones premezcladas — consulta la entrada de cada componente más abajo para su perfil completo.",

      "hero.healing.eyebrow": "Reparación de Tejido",
      "hero.healing.desc": "BPC-157, TB-500 y los demás péptidos de reparación de tejido — qué se ha estudiado realmente y qué no.",
      "hero.growth-hormone.eyebrow": "Eje Hipofisario",
      "hero.growth-hormone.desc": "Secretagogos y análogos de GHRH/GnRH, desde Ipamorelin hasta Sermorelin.",
      "hero.metabolic.eyebrow": "Peso y Metabolismo",
      "hero.metabolic.desc": "Semaglutida, Tirzepatida, Retatrutida y más — fármacos aprobados frente a compuestos en investigación.",
      "hero.cognitive.eyebrow": "Mente y Sueño",
      "hero.cognitive.desc": "Péptidos nootrópicos, ansiolíticos y relacionados con el sueño.",
      "hero.longevity.eyebrow": "Envejecimiento Celular",
      "hero.longevity.desc": "Péptidos bioreguladores y de investigación mitocondrial.",
      "hero.sexual-hormonal.eyebrow": "Salud Reproductiva",
      "hero.sexual-hormonal.desc": "Compuestos hormonales reproductivos y de salud sexual.",
      "hero.cosmetic.eyebrow": "Cuidado de la Piel",
      "hero.cosmetic.desc": "Ingredientes exclusivamente tópicos usados en formulaciones para el cuidado de la piel.",
      "hero.bone-muscle.eyebrow": "Salud Estructural",
      "hero.bone-muscle.desc": "Compuestos relacionados con la densidad ósea y la masa muscular.",
      "hero.steroids.eyebrow": "Gimnasio y Rendimiento",
      "hero.steroids.desc": "Testosterona, Trembolona, Dianabol y más — qué hacen, y sus serios riesgos legales y de salud.",
      "hero.other.eyebrow": "Investigación Especializada",
      "hero.other.desc": "Moduladores inmunológicos y compuestos de investigación/laboratorio especializados.",
      "hero.explore_btn": "Explorar categoría",

      "index.subheading": "Busca o filtra la lista completa de referencia a continuación.",
      "index.steroidsSubheading": "Busca o filtra los 14 esteroides anabólicos (EAA) del catálogo a continuación.",
      "index.search_placeholder": "Buscar por nombre o categoría…",

      "footer.about": "Una referencia en lenguaje sencillo sobre los péptidos que se discuten y venden en línea: qué es cada uno, para qué se usa y sus efectos secundarios conocidos o reportados. Este sitio no vende ningún producto.",
      "footer.browse": "Explorar",
      "footer.important": "Importante",
      "footer.not_advice": "No es asesoramiento médico",
      "footer.not_for_sale": "Nada en este sitio está a la venta",
      "footer.consult": "Consulta siempre a un profesional de la salud con licencia",
      "footer.edu_only": "Contenido exclusivamente educativo.",

      "disclaimer.headline": "Solo referencia educativa — nada en esta página está a la venta, y nada aquí es asesoramiento médico.",
      "disclaimer.body": "<ul><li>Esta página existe para que las personas puedan consultar qué es un péptido determinado, para qué se estudia o comercializa, y cuáles son sus efectos secundarios conocidos o reportados.</li><li>Muchos compuestos son medicamentos de venta exclusiva con receta que requieren supervisión médica, o productos químicos de investigación no aprobados con poca o ninguna información de seguridad en humanos.</li><li>Los péptidos vendidos en línea fuera de la cadena de suministro farmacéutico regulada no están verificados en cuanto a pureza, esterilidad o dosificación precisa.</li><li>Habla siempre con un profesional de la salud con licencia antes de considerar cualquiera de estos compuestos, y verifica las leyes aplicables donde vives.</li></ul>"
    }
  };

  function t(key) {
    if (LANG === "en") return null;
    return (TRANSLATIONS[LANG] && TRANSLATIONS[LANG][key]) || null;
  }

  function applyStaticTranslations(root) {
    var scope = root || document;
    Array.prototype.forEach.call(scope.querySelectorAll("[data-i18n]"), function (el) {
      if (!el.hasAttribute("data-i18n-original")) el.setAttribute("data-i18n-original", el.textContent);
      var key = el.getAttribute("data-i18n");
      var translated = t(key);
      el.textContent = translated !== null ? translated : el.getAttribute("data-i18n-original");
    });
    Array.prototype.forEach.call(scope.querySelectorAll("[data-i18n-html]"), function (el) {
      if (!el.hasAttribute("data-i18n-original-html")) el.setAttribute("data-i18n-original-html", el.innerHTML);
      var key = el.getAttribute("data-i18n-html");
      var translated = t(key);
      el.innerHTML = translated !== null ? translated : el.getAttribute("data-i18n-original-html");
    });
    Array.prototype.forEach.call(scope.querySelectorAll("[data-i18n-placeholder]"), function (el) {
      if (!el.hasAttribute("data-i18n-original-placeholder")) el.setAttribute("data-i18n-original-placeholder", el.getAttribute("placeholder") || "");
      var key = el.getAttribute("data-i18n-placeholder");
      var translated = t(key);
      el.setAttribute("placeholder", translated !== null ? translated : el.getAttribute("data-i18n-original-placeholder"));
    });
  }

  function setLang(lang) {
    LANG = lang === "es" ? "es" : "en";
    try { window.localStorage.setItem("peptideGuideLang", LANG); } catch (e) { /* ignore */ }
    document.documentElement.setAttribute("lang", LANG);
    applyStaticTranslations(document);
    Array.prototype.forEach.call(document.querySelectorAll("[data-lang-toggle] button"), function (btn) {
      btn.classList.toggle("is-active", btn.getAttribute("data-lang") === LANG);
    });
    REFRESH_CALLBACKS.forEach(function (fn) { fn(); });
  }

  function wireLangToggle() {
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-lang-toggle] button");
      if (!btn) return;
      setLang(btn.getAttribute("data-lang"));
    });
  }

  /* ================= Card rendering (language-aware) ================= */
  function badgeClass(status) {
    switch (status) {
      case "approved": return "badge-approved";
      case "investigational": return "badge-investigational";
      case "cosmetic": return "badge-cosmetic";
      case "caution": return "badge-caution";
      case "blend": return "badge-blend";
      case "supplement": return "badge-supplement";
      case "controlled": return "badge-controlled";
      default: return "badge-research";
    }
  }

  function escapeHtml(str) {
    return String(str || "").replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  var MOL_ICON_HTML =
    '<span class="mol-icon" aria-hidden="true"><span class="mol-icon-stage">' +
    '<i style="--rz:0deg"></i><i style="--rz:60deg"></i><i style="--rz:120deg"></i>' +
    '<i style="--rz:180deg"></i><i style="--rz:240deg"></i><i style="--rz:300deg"></i>' +
    '<i class="is-center"></i></span></span>';

  function field(item, key) {
    if (LANG === "es" && item[key + "_es"]) return item[key + "_es"];
    return item[key] || "";
  }

  function structureHtml(item, ui) {
    if (!item.pubchemCid) return "";
    var src = "https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/cid/" + item.pubchemCid + "/PNG?record_type=2d&image_size=200x200";
    var alt = ui.structureAlt.replace("{name}", item.name);
    return (
      '<div class="pep-structure">' +
        '<img src="' + src + '" alt="' + escapeHtml(alt) + '" loading="lazy" width="200" height="200" ' +
             'onerror="this.parentElement.style.display=\'none\'">' +
      "</div>" +
      '<div class="pep-structure-caption">' + escapeHtml(ui.structureCaption) + "</div>"
    );
  }

  function mechanismHtml(item, ui) {
    var tags = LANG === "es" && item.mechanism_es ? item.mechanism_es : item.mechanism;
    if (!tags || !tags.length) return "";
    var pills = tags.map(function (m) {
      return '<span class="mech-tag"><b>' + escapeHtml(m.t) + "</b> — " + escapeHtml(m.a) + "</span>";
    }).join("");
    return (
      '<div class="pep-mechanism">' +
        '<span class="pep-mechanism-label">' + escapeHtml(ui.mechanismLabel) + "</span>" +
        pills +
      "</div>"
    );
  }

  var FORM_UI_KEY = { injection: "formInjection", tablet: "formTablet", topical: "formTopical" };

  function formsHtml(item, ui) {
    var forms = item.forms;
    if (!forms || !forms.length) return "";
    var pills = forms.map(function (f) {
      var key = FORM_UI_KEY[f];
      var label = key ? ui[key] : f;
      return '<span class="form-pill form-pill-' + escapeHtml(f) + '">' + escapeHtml(label) + "</span>";
    }).join("");
    return '<div class="pep-forms">' + pills + "</div>";
  }

  function detailUrl(item) {
    return location.pathname + "?compound=" + encodeURIComponent(item.slug);
  }

  function priceCardHtml(item, ui) {
    if (!item.priceInfo || !item.priceInfo.range) return "";
    return '<p class="pep-card-price"><strong>' + escapeHtml(ui.priceCardLabel) + "</strong> " + escapeHtml(item.priceInfo.range) + "</p>";
  }

  function cardHtml(item) {
    var ui = UI_STRINGS[LANG] || UI_STRINGS.en;
    var statusLabel = LANG === "es" && item.statusLabel_es ? item.statusLabel_es : item.statusLabel;
    var category = LANG === "es" && item.category_es ? item.category_es : item.category;
    var purpose = field(item, "purpose");
    return (
      '<a class="pep-card-link" href="' + detailUrl(item) + '">' +
      '<article class="pep-card" data-bucket="' + escapeHtml(item.bucket) + '" data-name="' + escapeHtml(item.name.toLowerCase()) + '">' +
        structureHtml(item, ui) +
        '<span class="badge ' + badgeClass(item.status) + '">' + escapeHtml(statusLabel) + "</span>" +
        formsHtml(item, ui) +
        "<h3>" + escapeHtml(item.name) + "</h3>" +
        '<div class="pep-cat">' + escapeHtml(category) + "</div>" +
        mechanismHtml(item, ui) +
        '<p class="pep-card-purpose"><strong>' + escapeHtml(ui.purpose) + "</strong> " + escapeHtml(purpose) + "</p>" +
        priceCardHtml(item, ui) +
      "</article>" +
      "</a>"
    );
  }

  /* ---------------- Compound detail view (client-side "page", routed via ?compound=slug) ---------------- */
  function findItemBySlug(slug) {
    var all = DATA.peptides.concat(DATA.blends);
    for (var i = 0; i < all.length; i++) {
      if (all[i].slug === slug) return all[i];
    }
    return null;
  }

  function statusDetailText(status) {
    var copy = STATUS_DETAIL_COPY[status] || STATUS_DETAIL_COPY.research;
    return LANG === "es" ? copy.es : copy.en;
  }

  function formsDetailHtml(forms) {
    if (!forms || !forms.length) return "";
    return forms.map(function (f) {
      var copy = FORM_DETAIL_COPY[f];
      if (!copy) return "";
      return "<p>" + escapeHtml(LANG === "es" ? copy.es : copy.en) + "</p>";
    }).join("");
  }

  var currentDetailItem = null;

  function renderDetailView(item) {
    var root = document.querySelector("[data-detail-view]");
    if (!root || !item) return;
    var ui = UI_STRINGS[LANG] || UI_STRINGS.en;
    var name = item.name;
    var category = LANG === "es" && item.category_es ? item.category_es : item.category;
    var statusLabel = LANG === "es" && item.statusLabel_es ? item.statusLabel_es : item.statusLabel;
    var purpose = field(item, "purpose");
    var sideEffects = field(item, "sideEffects");
    var detailNotes = field(item, "detailNotes");
    var mechTags = LANG === "es" && item.mechanism_es ? item.mechanism_es : item.mechanism;

    var structureBlock = "";
    if (item.pubchemCid) {
      var src = "https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/cid/" + item.pubchemCid + "/PNG?record_type=2d&image_size=300x300";
      var pageUrl = "https://pubchem.ncbi.nlm.nih.gov/compound/" + item.pubchemCid;
      structureBlock =
        "<div>" +
          '<div class="pep-detail-structure"><img src="' + src + '" alt="' + escapeHtml(ui.structureAlt.replace("{name}", name)) + '" loading="lazy" onerror="this.parentElement.style.display=\'none\'"></div>' +
          '<div class="pep-detail-structure-caption">' + escapeHtml(ui.structureCaption) + "</div>" +
          '<div class="pep-detail-cid"><a href="' + pageUrl + '" target="_blank" rel="noopener">' + escapeHtml(ui.detailCidLabel.replace("{cid}", item.pubchemCid)) + "</a></div>" +
        "</div>";
    }

    var mechBlock = "";
    if (mechTags && mechTags.length) {
      mechBlock =
        '<div class="pep-detail-section">' +
          "<h2>" + escapeHtml(ui.mechanismLabel) + "</h2>" +
          '<div class="pep-detail-mech-list">' +
            mechTags.map(function (m) {
              return '<div class="pep-detail-mech-item"><b>' + escapeHtml(m.t) + "</b><span>" + escapeHtml(m.a) + "</span></div>";
            }).join("") +
          "</div>" +
        "</div>";
    }

    var formsBlock = "";
    var formsText = formsDetailHtml(item.forms);
    if (formsText) {
      formsBlock = '<div class="pep-detail-section"><h2>' + escapeHtml(ui.detailFormsHeading) + "</h2>" + formsText + "</div>";
    }

    var legalBlock = '<div class="pep-detail-section"><h2>' + escapeHtml(ui.detailLegalHeading) + "</h2><p>" + escapeHtml(statusDetailText(item.status)) + "</p></div>";

    var priceBlock = "";
    if (item.priceInfo) {
      if (item.priceInfo.range) {
        priceBlock =
          '<div class="pep-detail-section pep-detail-price">' +
            "<h2>" + escapeHtml(ui.priceHeading) + "</h2>" +
            '<p class="pep-detail-price-range">' + escapeHtml(item.priceInfo.range) + "</p>" +
            '<p class="pep-detail-price-note">' + escapeHtml(item.priceInfo.note) + "</p>" +
            '<p class="pep-detail-price-disclaimer">' + escapeHtml(ui.priceDisclaimer) + "</p>" +
          "</div>";
      } else {
        priceBlock =
          '<div class="pep-detail-section pep-detail-price pep-detail-price-none">' +
            "<h2>" + escapeHtml(ui.priceNoRangeHeading) + "</h2>" +
            '<p class="pep-detail-price-note">' + escapeHtml(item.priceInfo.note) + "</p>" +
          "</div>";
      }
    }

    var moreBlock = "";
    if (detailNotes) {
      moreBlock = '<div class="pep-detail-section"><h2>' + escapeHtml(ui.detailMoreHeading) + "</h2><p>" + escapeHtml(detailNotes) + "</p></div>";
    }

    var relatedList = DATA.peptides.filter(function (p) { return p.bucket === item.bucket && p.slug !== item.slug; }).slice(0, 8);
    var relatedBlock = "";
    if (relatedList.length) {
      relatedBlock =
        '<div class="pep-detail-section">' +
          "<h2>" + escapeHtml(ui.detailRelatedHeading) + "</h2>" +
          '<div class="pep-detail-related">' +
            relatedList.map(function (p) {
              return '<a href="' + detailUrl(p) + '">' + escapeHtml(p.name) + "</a>";
            }).join("") +
          "</div>" +
        "</div>";
    }

    root.innerHTML =
      '<div class="pep-detail">' +
        '<a class="pep-detail-back" href="' + escapeHtml(location.pathname) + '">' + escapeHtml(ui.detailBack) + "</a>" +
        '<div class="pep-detail-head">' +
          structureBlock +
          '<div class="pep-detail-title-block">' +
            '<div class="pep-detail-badges"><span class="badge ' + badgeClass(item.status) + '">' + escapeHtml(statusLabel) + "</span></div>" +
            formsHtml(item, ui) +
            "<h1>" + escapeHtml(name) + "</h1>" +
            '<div class="pep-detail-cat">' + escapeHtml(category) + "</div>" +
          "</div>" +
        "</div>" +
        mechBlock +
        '<div class="pep-detail-section"><h2>' + escapeHtml(ui.purpose) + "</h2><p>" + escapeHtml(purpose) + "</p></div>" +
        (sideEffects ? '<div class="pep-detail-section"><h2>' + escapeHtml(ui.sideEffects) + "</h2><p>" + escapeHtml(sideEffects) + "</p></div>" : "") +
        priceBlock +
        moreBlock +
        formsBlock +
        legalBlock +
        relatedBlock +
      "</div>";

    document.title = name + " — " + (document.title.split("·").pop() || "").trim();
  }

  function getQueryParam(name) {
    var match = new RegExp("[?&]" + name + "=([^&]*)").exec(location.search);
    return match ? decodeURIComponent(match[1].replace(/\+/g, " ")) : null;
  }

  function initDetailRouting() {
    function checkRoute() {
      var slug = getQueryParam("compound");
      var item = slug ? findItemBySlug(slug) : null;
      if (item) {
        currentDetailItem = item;
        document.body.classList.add("is-detail");
        var header = document.querySelector(".site-header");
        if (header) header.classList.add("is-scrolled");
        renderDetailView(item);
        window.scrollTo(0, 0);
      } else {
        currentDetailItem = null;
        document.body.classList.remove("is-detail");
      }
    }
    checkRoute();
    window.addEventListener("popstate", checkRoute);
    REFRESH_CALLBACKS.push(function () {
      if (currentDetailItem) renderDetailView(currentDetailItem);
    });
  }

  /* ---------------- Mobile nav ---------------- */
  document.addEventListener("click", function (e) {
    var toggle = e.target.closest("[data-nav-toggle]");
    if (!toggle) return;
    var nav = document.querySelector(toggle.getAttribute("data-nav-toggle"));
    if (nav) nav.classList.toggle("is-open");
  });

  /* ---------------- Generic scroll-snap carousel controls ---------------- */
  function wireCarouselControls(root) {
    var track = root.querySelector("[data-track]");
    if (!track) return;
    var prev = root.querySelector("[data-prev]");
    var next = root.querySelector("[data-next]");
    function step() {
      var card = track.querySelector(":scope > *");
      return card ? card.getBoundingClientRect().width + 16 : 300;
    }
    if (prev) prev.addEventListener("click", function () { track.scrollBy({ left: -step() * 2, behavior: "smooth" }); });
    if (next) next.addEventListener("click", function () { track.scrollBy({ left: step() * 2, behavior: "smooth" }); });
  }

  /* ---------------- Sticky header: transparent-over-hero -> solid on scroll ---------------- */
  function initHeaderScroll() {
    var header = document.querySelector(".site-header");
    if (!header) return;
    function update() {
      header.classList.toggle("is-scrolled", window.scrollY > 60 || document.body.classList.contains("is-detail"));
    }
    update();
    window.addEventListener("scroll", update, { passive: true });
  }

  /* ---------------- Hero slideshow (cinematic slide-switcher + destination rail) ---------------- */
  var SLIDE_DURATION = 5500;

  function initHero2(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll("[data-hero2-slide]"));
    if (!slides.length) return;

    var progressFill = root.querySelector("[data-hero2-progress]");
    var rail = root.querySelector("[data-hero2-rail]");
    var currentEl = root.querySelector("[data-hero2-current]");
    var prevBtn = root.querySelector("[data-hero2-prev]");
    var nextBtn = root.querySelector("[data-hero2-next]");
    var current = 0;
    var timer = null;

    function pad(n) { return (n < 10 ? "0" : "") + n; }

    function railLabel(slide, attr) {
      var bucket = slide.getAttribute("data-bucket");
      var key = attr === "eyebrow" ? "hero." + bucket + ".eyebrow" : "cat." + bucket + ".title";
      var translated = t(key);
      return translated !== null ? translated : slide.getAttribute("data-" + attr);
    }

    function renderRail() {
      if (!rail) return;
      var visibleCount = 4;
      var html = "";
      for (var i = 1; i <= visibleCount; i++) {
        var idx = (current + i) % slides.length;
        if (idx === current) break;
        var s = slides[idx];
        html +=
          '<div class="hero2-card" data-hero2-card data-goto="' + idx + '" style="background-image:' + s.getAttribute("data-bg") + ';">' +
            '<div class="hero2-card-label">' +
              MOL_ICON_HTML +
              '<span class="eyebrow">' + escapeHtml(railLabel(s, "eyebrow")) + "</span>" +
              '<span class="title">' + escapeHtml(railLabel(s, "title")) + "</span>" +
            "</div>" +
          "</div>";
      }
      rail.innerHTML = html;
      Array.prototype.forEach.call(rail.querySelectorAll("[data-goto]"), function (card) {
        card.addEventListener("click", function () {
          goTo(parseInt(card.getAttribute("data-goto"), 10));
          restart();
        });
      });
    }

    function runProgress() {
      if (!progressFill) return;
      progressFill.classList.remove("is-animating");
      progressFill.style.transitionDuration = "0ms";
      progressFill.style.width = "0%";
      // force reflow so the next transition actually runs
      void progressFill.offsetWidth;
      progressFill.classList.add("is-animating");
      progressFill.style.transitionDuration = SLIDE_DURATION + "ms";
      progressFill.style.width = "100%";
    }

    function setActive(i) {
      current = (i + slides.length) % slides.length;
      slides.forEach(function (s, idx) { s.classList.toggle("is-active", idx === current); });
      if (currentEl) currentEl.textContent = pad(current + 1);
      renderRail();
      runProgress();
    }

    function goTo(i) { setActive(i); }

    function restart() {
      if (timer) clearInterval(timer);
      if (slides.length > 1) {
        timer = setInterval(function () { setActive(current + 1); }, SLIDE_DURATION);
      }
    }

    if (prevBtn) prevBtn.addEventListener("click", function () { setActive(current - 1); restart(); });
    if (nextBtn) nextBtn.addEventListener("click", function () { setActive(current + 1); restart(); });

    setActive(0);
    restart();
    REFRESH_CALLBACKS.push(renderRail);
  }

  /* ---------------- Category carousel (renders from JSON) ---------------- */
  function initPeptideCarousel(root) {
    var bucket = root.getAttribute("data-bucket");
    var track = root.querySelector("[data-track]");
    if (!track) return;

    function render() {
      var ui = UI_STRINGS[LANG] || UI_STRINGS.en;
      var list = DATA.peptides.filter(function (p) { return p.bucket === bucket; });
      if (bucket === "blends") list = DATA.blends;
      if (!list.length) {
        track.innerHTML = '<p class="empty-note">' + escapeHtml(ui.emptyCategory) + "</p>";
        return;
      }
      track.innerHTML = list.map(cardHtml).join("");
    }

    render();
    wireCarouselControls(root);
    REFRESH_CALLBACKS.push(render);
  }

  /* ---------------- All-peptides searchable index ---------------- */
  function initIndex(root) {
    var grid = root.querySelector("[data-grid]");
    var search = root.querySelector("[data-search]");
    var pillsWrap = root.querySelector("[data-pills]");
    var countEl = root.querySelector("[data-count]");
    if (!grid) return;

    var lockBucket = root.getAttribute("data-lock-bucket") || "";
    var all = DATA.peptides.concat(DATA.blends.map(function (b) { return b; }));
    if (lockBucket) all = all.filter(function (item) { return item.bucket === lockBucket; });
    var activeBucket = "all";

    function bucketLabel(key) {
      if (key === "blends") return (UI_STRINGS[LANG] || UI_STRINGS.en).pillBlends;
      var meta = DATA.buckets[key];
      if (!meta) return key;
      return LANG === "es" && meta.label_es ? meta.label_es : meta.label;
    }

    function renderPills() {
      if (!pillsWrap) return;
      pillsWrap.innerHTML = "";
      if (lockBucket) {
        pillsWrap.style.display = "none";
        return;
      }
      pillsWrap.style.display = "";
      var ui = UI_STRINGS[LANG] || UI_STRINGS.en;
      var buckets = Object.keys(DATA.buckets);
      buckets.push("blends");
      var allBtn = document.createElement("button");
      allBtn.textContent = ui.pillAll;
      allBtn.className = activeBucket === "all" ? "is-active" : "";
      allBtn.addEventListener("click", function () { setBucket("all"); });
      pillsWrap.appendChild(allBtn);
      buckets.forEach(function (key) {
        var b = document.createElement("button");
        b.textContent = bucketLabel(key);
        b.className = activeBucket === key ? "is-active" : "";
        b.addEventListener("click", function () { setBucket(key); });
        pillsWrap.appendChild(b);
      });
    }

    function setBucket(key) {
      activeBucket = key;
      renderPills();
      render();
    }

    function render() {
      var ui = UI_STRINGS[LANG] || UI_STRINGS.en;
      var q = (search && search.value || "").trim().toLowerCase();
      var filtered = all.filter(function (item) {
        var matchesBucket = activeBucket === "all" || item.bucket === activeBucket;
        var matchesQuery = !q || item.name.toLowerCase().indexOf(q) > -1 || (item.category || "").toLowerCase().indexOf(q) > -1;
        return matchesBucket && matchesQuery;
      });
      grid.innerHTML = filtered.length ? filtered.map(cardHtml).join("") : '<p class="empty-note">' + escapeHtml(ui.emptySearch) + "</p>";
      if (countEl) countEl.textContent = ui.countTemplate.replace("{shown}", filtered.length).replace("{total}", all.length);
    }

    if (search) search.addEventListener("input", render);
    renderPills();
    render();
    REFRESH_CALLBACKS.push(function () { renderPills(); render(); });
  }

  document.addEventListener("DOMContentLoaded", function () {
    document.documentElement.setAttribute("lang", LANG);
    initHeaderScroll();
    wireLangToggle();
    applyStaticTranslations(document);
    document.querySelectorAll("[data-hero2]").forEach(initHero2);
    document.querySelectorAll("[data-peptide-carousel]").forEach(initPeptideCarousel);
    document.querySelectorAll("[data-peptide-index]").forEach(initIndex);
    initDetailRouting();
    if (LANG === "es") {
      Array.prototype.forEach.call(document.querySelectorAll("[data-lang-toggle] button"), function (btn) {
        btn.classList.toggle("is-active", btn.getAttribute("data-lang") === LANG);
      });
    }
  });
})();
